import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, Bell, Archive, CheckCircle, ChevronRight, ChevronLeft } from "lucide-react";
import Logo from "../images/Logo.png";
import NetworkCanvas from "./NetworkCanvas";
import UploadLoading from "./UploadLoading";
import { saveUserSettings } from "../service/UserSettingsService";
import { fetchSinglePresignedUrl, insertSingleManuscript } from "../service/ManuscriptService";
import { errorMessage } from "../service/HttpErrors";
import { QUICK, SAMPLE } from "../constants/Constants";
import { useTheme } from "./ThemeContext";

const TOTAL_STEPS = 4;
const GREEN = "var(--brand)";
const MUTED = "#b0cac6";

export default function Onboard() {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [step, setStep] = useState(1);
    const [userSettings, setUserSettings] = useState({
        notifications: true,
        storeManuscripts: false,
        showScoreHistory: true,
        peerReviewNotifications: true,
        openToReview: false,
        featuredEligible: false,
        featuredShowAuthor: false,
        featuredAllowDownload: false,
        feedOptIn: false,
        profilePublic: false,
    });
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [authors, setAuthors] = useState("");
    const [runType, setRunType] = useState(QUICK);
    const [dragOver, setDragOver] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [email, setEmail] = useState(null);
    const [notifications, setNotifications] = useState(false);
    const fileInputRef = useRef(null);
    const apiDoneRef = useRef(null);
    const animDoneRef = useRef(false);

    const canContinue = step !== 3 || runType === SAMPLE || !!file;

    function handleFile(f) {
        if (!f || f.type !== "application/pdf") { setError("Please upload a PDF file."); return; }
        if (f.size > 50 * 1024 * 1024) { setError("File must be under 50 MB."); return; }
        setFile(f);
        setError("");
    }

    function finalize(result) {
        if (result === "sample") {
            navigate("/sample/submission");
        } else if (typeof result === "object") {
            setEmail(result.email ?? null);
            setNotifications(result.notifications ?? false);
            setSubmitted(true);
            setLoading(false);
            setStep(4);
        } else {
            setLoading(false);
            setError(result || "Something went wrong. Please try again.");
        }
    }

    function handleAnimDone() {
        animDoneRef.current = true;
        if (apiDoneRef.current !== null) finalize(apiDoneRef.current);
    }

    async function handleSubmit() {
        setLoading(true);
        setError("");
        apiDoneRef.current = null;
        animDoneRef.current = false;

        if (runType === SAMPLE) {
            try {
                await saveUserSettings(userSettings, true);
            } catch { /* best effort */ }
            apiDoneRef.current = "sample";
            if (animDoneRef.current) finalize("sample");
            return;
        }

        try {
            const settingsRes = await saveUserSettings(userSettings, true);
            if (!settingsRes.settings) throw new Error();
            const presigned = await fetchSinglePresignedUrl(true);
            if (!presigned?.presignedUrl?.url) throw new Error();
            const uploadResp = await fetch(presigned.presignedUrl.url, {
                method: "PUT",
                headers: { "Content-Type": "application/pdf" },
                body: file,
            });
            if (!uploadResp.ok) throw new Error();
            const insertRes = await insertSingleManuscript(presigned.presignedUrl.key, file.name, title, authors, true);
            if (!insertRes.success || !insertRes.email) throw new Error();
            apiDoneRef.current = { email: insertRes.email, notifications: insertRes.notifications };
        } catch (err) {
            apiDoneRef.current = errorMessage(err, "Something went wrong. Please try again.");
        }
        if (animDoneRef.current) finalize(apiDoneRef.current);
    }

    function handleContinue() {
        if (step === 3) { handleSubmit(); return; }
        setStep(s => Math.min(TOTAL_STEPS, s + 1));
    }

    // ─── Step content ──────────────────────────────────────────

    function StepWelcome() {
        return (
            <div className="onb-step">
                <div className="onb-icon-wrap" style={{ background: "var(--accent-soft)" }}>
                    <img src={Logo} alt="Logo" className="theme-invert" style={{ width: 40, height: 40, objectFit: "contain" }} />
                </div>
                <h2 className="onb-title">Welcome to DeepThought</h2>
                <p className="onb-body">
                    DeepThought analyzes your research manuscripts and generates scores for novelty,
                    methodology, and impact — helping you understand where your work fits in the
                    broader scientific landscape.
                </p>
                <p className="onb-body">
                    This quick setup will configure your preferences and optionally submit your
                    first paper for analysis.
                </p>
            </div>
        );
    }

    function StepPreferences() {
        return (
            <div className="onb-step">
                <div className="onb-icon-wrap" style={{ background: "var(--blue-bg)" }}>
                    <Bell size={22} color="var(--blue)" />
                </div>
                <h2 className="onb-title">Your Preferences</h2>
                <p className="onb-body">Customize how DeepThought works for you.</p>
                <div className="onb-prefs">
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, notifications: !s.notifications }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Email Notifications</p>
                            <p className="onb-pref-sub">Get notified when your manuscript finishes processing</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.notifications ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.notifications ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, storeManuscripts: !s.storeManuscripts }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Store Manuscripts</p>
                            <p className="onb-pref-sub">Keep uploaded files after analysis — required for peer review and later download</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.storeManuscripts ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.storeManuscripts ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, showScoreHistory: !s.showScoreHistory }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Show Score History</p>
                            <p className="onb-pref-sub">Display the score history chart on your dashboard</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.showScoreHistory ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.showScoreHistory ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, peerReviewNotifications: !s.peerReviewNotifications }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Peer Review Notifications</p>
                            <p className="onb-pref-sub">Get notified when your peer review status changes</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.peerReviewNotifications ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.peerReviewNotifications ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, openToReview: !s.openToReview }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Available as a Peer Reviewer</p>
                            <p className="onb-pref-sub">Signal that you're willing to review other researchers' manuscripts</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.openToReview ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.openToReview ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, feedOptIn: !s.feedOptIn }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Share Wins on the Community Feed</p>
                            <p className="onb-pref-sub">Let other researchers see your high scores, milestones, and personal bests</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.feedOptIn ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.feedOptIn ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({ ...s, profilePublic: !s.profilePublic }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Make My Profile Public</p>
                            <p className="onb-pref-sub">Anyone with the link can view your stats and submissions (read-only)</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.profilePublic ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.profilePublic ? 21 : 3 }} />
                        </div>
                    </div>
                    <div className="onb-pref-row" onClick={() => setUserSettings(s => ({
                        ...s,
                        featuredEligible: !s.featuredEligible,
                        featuredShowAuthor: !s.featuredEligible ? s.featuredShowAuthor : false,
                        featuredAllowDownload: !s.featuredEligible ? s.featuredAllowDownload : false,
                    }))}>
                        <div className="onb-pref-text">
                            <p className="onb-pref-label">Eligible for Paper of the Week</p>
                            <p className="onb-pref-sub">Top-scoring submissions get featured on every researcher's dashboard</p>
                        </div>
                        <div className="onb-toggle" style={{ background: userSettings.featuredEligible ? GREEN : "var(--toggle-off-cool)" }}>
                            <span className="onb-toggle-knob" style={{ left: userSettings.featuredEligible ? 21 : 3 }} />
                        </div>
                    </div>
                    {userSettings.featuredEligible && (
                        <>
                            <div className="onb-pref-row onb-pref-sub-row" onClick={() => setUserSettings(s => ({ ...s, featuredShowAuthor: !s.featuredShowAuthor }))}>
                                <div className="onb-pref-text">
                                    <p className="onb-pref-label">Show my name</p>
                                    <p className="onb-pref-sub">Otherwise the card shows "Anonymous Researcher"</p>
                                </div>
                                <div className="onb-toggle" style={{ background: userSettings.featuredShowAuthor ? GREEN : "var(--toggle-off-cool)" }}>
                                    <span className="onb-toggle-knob" style={{ left: userSettings.featuredShowAuthor ? 21 : 3 }} />
                                </div>
                            </div>
                            <div className="onb-pref-row onb-pref-sub-row" onClick={() => setUserSettings(s => ({ ...s, featuredAllowDownload: !s.featuredAllowDownload }))}>
                                <div className="onb-pref-text">
                                    <p className="onb-pref-label">Allow viewers to download the PDF</p>
                                    <p className="onb-pref-sub">Requires "Store Manuscripts" to be on</p>
                                </div>
                                <div className="onb-toggle" style={{ background: userSettings.featuredAllowDownload ? GREEN : "var(--toggle-off-cool)" }}>
                                    <span className="onb-toggle-knob" style={{ left: userSettings.featuredAllowDownload ? 21 : 3 }} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    function StepUpload() {
        return (
            <div className="onb-step">
                <div className="onb-icon-wrap" style={{ background: "var(--accent-soft)" }}>
                    <Upload size={22} color="#22c55e" />
                </div>
                <h2 className="onb-title">Upload Your First Paper</h2>
                <p className="onb-body">Upload a manuscript or explore with a sample report — you can always do this later.</p>

                <div className="onb-run-types">
                    <div
                        className={`onb-run-type ${runType === QUICK ? "onb-run-type-active" : ""}`}
                        onClick={() => setRunType(QUICK)}
                    >
                        <FileText size={18} color={runType === QUICK ? "var(--brand-text)" : "var(--ink-300)"} />
                        <div>
                            <p className="onb-run-label">Upload My Paper</p>
                            <p className="onb-run-sub">Analyze your own manuscript</p>
                        </div>
                    </div>
                    <div
                        className={`onb-run-type ${runType === SAMPLE ? "onb-run-type-active" : ""}`}
                        onClick={() => { setRunType(SAMPLE); setError(""); }}
                    >
                        <Archive size={18} color={runType === SAMPLE ? "var(--brand-text)" : "var(--ink-300)"} />
                        <div>
                            <p className="onb-run-label">View Sample Report</p>
                            <p className="onb-run-sub">Explore with a pre-loaded paper</p>
                        </div>
                    </div>
                </div>

                <div
                    className={`onb-dropzone ${dragOver ? "onb-dropzone-over" : ""} ${file ? "onb-dropzone-filled" : ""} ${runType === SAMPLE ? "onb-dropzone-muted" : ""}`}
                    onClick={() => runType === QUICK && fileInputRef.current?.click()}
                    onDragOver={e => { if (runType === QUICK) { e.preventDefault(); setDragOver(true); } }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); if (runType === QUICK) handleFile(e.dataTransfer.files[0]); }}
                >
                    {file && runType === QUICK ? (
                        <>
                            <CheckCircle size={20} color="var(--brand-text)" />
                            <span className="onb-dropzone-filename">{file.name}</span>
                            <span className="onb-dropzone-change">Click to change</span>
                        </>
                    ) : (
                        <>
                            <Upload size={20} color={runType === SAMPLE ? "var(--ink-200)" : "var(--ink-300)"} />
                            <span className="onb-dropzone-hint" style={{ color: runType === SAMPLE ? "var(--ink-200)" : undefined }}>Drop PDF here or <u>browse</u></span>
                            <span className="onb-dropzone-limit">Max 50 MB</span>
                        </>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={e => handleFile(e.target.files[0])}
                    />
                </div>

                <div className="onb-fields">
                    <div className="onb-field">
                        <label className="upload-label">Title <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                        <input
                            className="upload-input"
                            type="text"
                            placeholder="Manuscript title"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            disabled={runType === SAMPLE}
                        />
                    </div>
                    <div className="onb-field">
                        <label className="upload-label">Authors <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                        <input
                            className="upload-input"
                            type="text"
                            placeholder="Author names, comma-separated"
                            value={authors}
                            onChange={e => setAuthors(e.target.value)}
                            disabled={runType === SAMPLE}
                        />
                    </div>
                </div>

                {error && <p className="chpw-error" style={{ marginTop: 8 }}>{error}</p>}
            </div>
        );
    }

    function StepComplete() {
        return (
            <div className="onb-step" style={{ textAlign: "center" }}>
                <div className="onb-icon-wrap" style={{ background: "var(--accent-soft)", margin: "0 auto 20px" }}>
                    <CheckCircle size={28} color="#22c55e" />
                </div>
                <h2 className="onb-title">You're all set!</h2>
                {submitted ? (
                    <>
                        <p className="onb-body">Looks like we have quite a few papers waiting to score! We have received your paper and will process it shortly.</p>
                        {notifications && email ? (
                            <p className="onb-body">
                                We'll send a notification to <strong>{email}</strong> when processing is complete. Check your spam folder just in case.
                            </p>
                        ) : (
                            <p className="onb-body">
                                Check the <strong>Submissions</strong> page to track processing status.
                            </p>
                        )}
                    </>
                ) : (
                    <p className="onb-body">
                        Your preferences have been saved. You can upload papers anytime from the dashboard.
                    </p>
                )}
            </div>
        );
    }

    const steps = [StepWelcome, StepPreferences, StepUpload, StepComplete];
    const CurrentStep = steps[step - 1];

    return (
        <div className="onb-page">
            <div className="onb-canvas-bg">
                <NetworkCanvas />
            </div>

            <div className="onb-card">
                {/* Header */}
                <div className="onb-card-header">
                    <img src={Logo} alt="Logo" className="theme-invert" style={{ width: 24, height: 24, objectFit: "contain" }} />
                    <div className="onb-dots">
                        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                            <span key={i} className={`onb-dot ${i + 1 === step ? "onb-dot-active" : i + 1 < step ? "onb-dot-done" : ""}`} />
                        ))}
                    </div>
                    <span className="onb-step-label">Step {step} of {TOTAL_STEPS}</span>
                </div>

                {/* Body */}
                <div className="onb-card-body">
                    {loading ? <UploadLoading onDone={handleAnimDone} /> : <CurrentStep />}
                </div>

                {/* Footer */}
                <div className="onb-card-footer">
                    <div className="onb-footer-left">
                        {step > 1 && step < TOTAL_STEPS && !loading && (
                            <button className="onb-btn-back" onClick={() => setStep(s => s - 1)}>
                                <ChevronLeft size={15} /> Back
                            </button>
                        )}
                    </div>
                    <div className="onb-footer-right">
                        {step < TOTAL_STEPS && (
                            <button className="onb-btn-skip" onClick={() => navigate("/")}>
                                Skip
                            </button>
                        )}
                        {step < TOTAL_STEPS ? (
                            <button
                                className="onb-btn-continue"
                                disabled={!canContinue || loading}
                                style={{ background: canContinue && !loading ? GREEN : (theme === "dark" ? "#2f3d3a" : MUTED), cursor: canContinue && !loading ? "pointer" : "not-allowed" }}
                                onClick={handleContinue}
                            >
                                {loading ? "Processing…" : step === 3 ? "Start Analysis" : "Continue"}
                                {!loading && <ChevronRight size={15} />}
                            </button>
                        ) : (
                            <button
                                className="onb-btn-continue"
                                style={{ background: GREEN }}
                                onClick={() => navigate("/submissions")}
                            >
                                View Submissions <ChevronRight size={15} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
